import approach from '../../../assets/images/technology/approach.webp'
import technologyline from '../../../assets/images/technology/technologyline.svg'
import technologylinereverse from '../../../assets/images/technology/technologylinereverse.svg'
import Constants from '../../../app/utils/constants'
import { useState, useEffect, useRef } from 'react'
import colors from '../../theme/color'
import { motion } from 'framer-motion'

const ApproachSection = () => {
  const { approachItems } = Constants

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down')

  // 🔥 GLOW refs
  const blobRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInside, setIsInside] = useState(false)

  // ✅ Detect scroll direction
  useEffect(() => {
    let lastScrollY = window.scrollY

    const handleScroll = () => {
      if (window.scrollY > lastScrollY) {
        setScrollDirection('down')
      } else {
        setScrollDirection('up')
      }
      lastScrollY = window.scrollY
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 🔥 Cursor glow effect
  useEffect(() => {
    let mouseX = 0
    let mouseY = 0
    let currentX = 0
    let currentY = 0

    const container = containerRef.current
    if (!container) return

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      mouseX = e.clientX - rect.left
      mouseY = e.clientY - rect.top
    }

    const handleEnter = () => setIsInside(true)
    const handleLeave = () => setIsInside(false)

    const animate = () => {
      currentX += (mouseX - currentX) * 0.08
      currentY += (mouseY - currentY) * 0.08

      if (blobRef.current) {
        blobRef.current.style.transform = `
          translate(${currentX}px, ${currentY}px)
          translate(-50%, -50%)
        `
      }

      requestAnimationFrame(animate)
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)

    animate()

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
    }
  }, [])

  return (
    <section className="flex w-full items-center overflow-x-hidden">
      <div className="w-full">
        <h2 className="text-center font-rajdhani text-3xl font-semibold text-white sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
          Our Engineering Approach
        </h2>

        <div
          ref={containerRef}
          className="relative mt-8 w-full overflow-hidden p-5 sm:p-8 md:mt-10 md:p-10 lg:p-12"
          style={{ backgroundColor: colors.technology }}
        >
          {/* 🔥 CURSOR GLOW */}
          <div
            ref={blobRef}
            className={`pointer-events-none absolute w-[500px] h-[500px] rounded-full 
              bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)] 
              blur-3xl transition-all duration-500 ease-out
              ${isInside ? 'opacity-60 scale-100' : 'opacity-0 scale-75'}`}
            style={{ transform: 'translate(-50%, -50%)' }}
          />

          {/* LEFT STATIC GLOW (unchanged) */}
          <span
            aria-hidden
            className="pointer-events-none absolute -left-14 -top-14 h-[54%] w-[30%] rounded-full blur-3xl"
            style={{ backgroundColor: `${colors.purple}25` }}
          />

          <div className="grid items-stretch gap-4 lg:grid-cols-2">

            {/* LEFT CONTENT (UNCHANGED) */}
            <div className="mx-auto my-auto flex w-full max-w-[560px] flex-col py-1 lg:py-6">
              {approachItems.map((item, index) => {
                const isLeftStep = index % 2 === 0

                return (
                  <motion.div
                    key={item.title}
                    className="flex flex-col"
                    initial={{
                      opacity: 0,
                      y: scrollDirection === 'down' ? 60 : -60,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                      ease: 'easeOut',
                    }}
                  >
                    <div
                      className={`group flex min-h-[64px] justify-start ${
                        isLeftStep
                          ? 'ml-0 mnd:-ml-2'
                          : 'ml-[100px] md:ml-[120px] lg:ml-[130px] xl:ml-[120px] 2xl:ml-[120px]'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          className="my-auto flex h-15 w-15 shrink-0 items-center justify-center rounded-full bg-purple/15 transition-colors duration-300 hover:bg-purple md:h-20 md:w-20 lg:h-[64px] lg:w-[64px] xl:h-20 xl:w-20"
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                        >
                          <img
                            src={item.icon}
                            alt=""
                            className={`h-8 w-8 object-contain transition duration-300 md:h-10 md:w-10 lg:h-6 lg:w-6 xl:h-8 xl:w-8 ${
                              hoveredIndex === index ? 'brightness-0 invert' : ''
                            }`}
                          />
                        </button>

                        <div
                          className={`my-auto flex h-30 w-full  flex-col duration-700 ease-in-out overflow-hidden ${
                            hoveredIndex === index
                              ? 'items-start justify-start text-start'
                              : 'justify-center'
                          }`}
                        >
                          <h3 className="font-gilroy-medium  text-white text-base sm:text-lg md:text-xl lg:text-xl xl:text-2xl">
                            {item.title}
                          </h3>

                          <p
  className={`mt-1.5 leading-tight font-gilroy-medium text-xs md:text-base 2xl:text-base text-white/80 
  overflow-hidden transition-all duration-700 ease-in-out
  ${
    hoveredIndex === index
      ? 'max-h-[500px] max-w-full opacity-100 translate-y-0'
      : 'max-h-0 opacity-0 -translate-y-2'
  }`}
>
  {item.description}
</p>
                        
                        
                        </div>

                        
                      </div>

                    </div>

                    {index < approachItems.length - 1 && (
                      <img
                        src={
                          index === 1
                            ? technologylinereverse
                            : technologyline
                        }
                        alt=""
                        className={`h-auto w-[92px] sm:w-[108px] md:w-[124px] ${
                          isLeftStep
                            ? 'self-start ml-10 -translate-y-1'
                            : 'self-start ml-10 translate-y-0'
                        }`}
                      />
                    )}
                  </motion.div>
                )
              })}
            </div>

            {/* RIGHT IMAGE */}
            <div className="relative my-auto mx-auto hidden h-[280px] w-full max-w-[540px] overflow-hidden rounded-3xl lg:block lg:h-[500px] xl:h-[550px] 2xl:h-[600px]">
  <img
    src={approach}
    alt="Approach illustration"
    className="absolute inset-0 h-full w-full object-cover opacity-80"
  />
</div>

          </div>
        </div>
      </div>
    </section>
  )
}

export default ApproachSection